import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch } from '@store';
import { Account, PermissionEnum } from '@ui/src';
import {
  useLazyGetCorporateMainChannelQuery,
  useLazyGetCorporateAccountBalanceQuery,
  useLazyGetCorporateUserQuery,
} from '../../store/services/corporate';
import {
  useLazyGetCustomersTopUpQuery,
  useLazyGetCustomerUserScoreQuery,
} from '../../../customers/store/services/customers';
import { useGetCustomersInfoDictionariesQuery } from '../../../../store/dictionaries/services';
import { Balance, CustomerScore } from '../../../common/types';
import {
  clearGeneralState,
  setBalances,
} from '../../store/slices/general-info';
import { clearOverviewState } from '../../store/slices/overview';
import { clearAccountsState } from '../../../common/store/slices/accounts';
import { clearAmlState } from '../../../common/store/slices/aml';
import { clearStatisticState } from '../../../common/store/slices/statistics';
import {
  clearAccountsTransactionState,
  clearCardsTransactionState,
} from '../../../common/store/slices/statements';
import { clearDocumentsState } from '../../../common/store/slices/documents/documents';
import { clearTariffsState } from '../../../common/store/slices/tariff/tariff';
import { clearExternalAccounts } from '../../../common/store/slices/external-integration';
import { clearTopUpOptions } from '../../../common/store/slices/top-up-panel';
import { CorporateGeneralInfo } from '../../types';
import { clearBeneficiariesState } from '../../../common/store/slices/beneficiaries';

const useInformation = ({
  customerId,
  activePermissions,
  baseInformation,
  topUpOptions,
}: {
  customerId?: string;
  activePermissions?: PermissionEnum[];
  baseInformation?: Partial<CorporateGeneralInfo>;
  topUpOptions?: {
    companyName: string;
    id: string;
    visibleToClients: boolean;
  }[];
}) => {
  const [notFound, setNotFound] = useState(false);
  const [query, setQuery] = useState<string | undefined>(undefined);

  const { search } = useLocation();
  const dispatch = useAppDispatch();

  const [getCorporateUser, { isLoading, isFetching }] =
    useLazyGetCorporateUserQuery();
  const [getBalance] = useLazyGetCorporateAccountBalanceQuery();
  const [getCustomersTopUp] = useLazyGetCustomersTopUpQuery();
  const [getCorporateMainChannel] = useLazyGetCorporateMainChannelQuery();
  const [getCorporateScore] = useLazyGetCustomerUserScoreQuery();

  useGetCustomersInfoDictionariesQuery();

  const getBalancesAction = useCallback(async (accounts?: Account[]) => {
    if (accounts && accounts.length) {
      const balancesResponse = await Promise.all(
        accounts.map(async (item) => {
          const response = await getBalance(item.id);

          return response?.data?.data;
        }),
      );

      const formatted: Balance[] = [];

      balancesResponse.forEach((item) => {
        item?.edges.forEach((balance) => {
          formatted.push(balance.node);
        });
      });

      dispatch(
        setBalances({
          balances: formatted,
          totalBCBalances: (accounts
            ? accounts.map((item) => item.totalBalanceBC)
            : [0]) as number[],
        }),
      );
    }
  }, []);

  const getUserAction = useCallback(async () => {
    const { error } = (await getCorporateUser(customerId as string)) as {
      data: any;
      error: any;
    };
    const amlUpdateId = localStorage.getItem('amlCorporateUpdateId');

    if (error?.data) {
      if (error.data.statusCode === 400) {
        setNotFound(true);
      }
    } else if (
      !error &&
      !amlUpdateId &&
      activePermissions?.includes(PermissionEnum.CORPORATE_CUSTOMERS_SCORE_LIST)
    ) {
      (await getCorporateScore(customerId as string)) as {
        data: { data: CustomerScore };
      };
    }
  }, []);

  const onClear = () => {
    dispatch(clearGeneralState());
    dispatch(clearOverviewState());
    dispatch(clearAccountsState());
    dispatch(clearAmlState());
    dispatch(clearStatisticState());
    dispatch(clearAccountsTransactionState());
    dispatch(clearCardsTransactionState());
    dispatch(clearDocumentsState());
    dispatch(clearTariffsState());
    dispatch(clearExternalAccounts());
    dispatch(clearBeneficiariesState());
    dispatch(clearTopUpOptions());
  };

  useEffect(() => {
    if (customerId) {
      getUserAction();
      getCorporateMainChannel(customerId);
    }
  }, [customerId]);

  useEffect(() => {
    if (query === undefined) {
      setQuery(search);
    }
  }, [search, query]);

  useEffect(() => {
    if (baseInformation) {
      getBalancesAction(baseInformation?.accounts);

      if (!topUpOptions) {
        getCustomersTopUp({});
      }
    }
  }, [baseInformation]);

  useEffect(
    () => () => {
      onClear();
    },
    [dispatch],
  );

  return {
    isLoading,
    isFetching,
    notFound,
    query,
    onClear,
  };
};

export default useInformation;
